<template>
  <div>
  <v-menu v-model="menuOpen" offset-y :close-on-content-click="false" left nudge-bottom="8">
    <template v-slot:activator="{ on, attrs }">
      <v-btn v-bind="attrs" v-on="on" icon class="menu-app-btn a11y-keep" aria-label="Accessibility Menu">
        <span class="a11y-pentagon-bg">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="4.5" r="2" fill="#fff" />
            <path d="M12 7.5C9 7.5 6.5 8.5 6.5 8.5L7.5 10.5C7.5 10.5 9.5 9.5 12 9.5C14.5 9.5 16.5 10.5 16.5 10.5L17.5 8.5C17.5 8.5 15 7.5 12 7.5Z" fill="#fff" />
            <path d="M10.5 11L9 17.5L7 22" stroke="#fff" stroke-width="1.8" stroke-linecap="round" />
            <path d="M13.5 11L15 17.5L17 22" stroke="#fff" stroke-width="1.8" stroke-linecap="round" />
            <path d="M12 11V15" stroke="#fff" stroke-width="1.8" stroke-linecap="round" />
          </svg>
          <span v-if="hasActiveSettings" class="a11y-trigger-dot"></span>
        </span>
      </v-btn>
    </template>

    <v-card class="a11y-card" width="380px">
      <!-- Header -->
      <div class="a11y-header d-flex align-center justify-space-between px-4 py-3">
        <div class="d-flex align-center" style="gap: 8px;">
          <span class="a11y-title">Accessibility</span>
        </div>
        <v-btn icon small @click="closeMenu" class="a11y-close-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#0037B7" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </v-btn>
      </div>

      <!-- Grid -->
      <div class="a11y-grid px-3 pb-1">
        <div class="a11y-item" :class="{ active: screenReaderOn }" @click="toggleScreenReader()">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M15.54 8.46C16.48 9.4 17.01 10.67 17.01 12C17.01 13.33 16.48 14.6 15.54 15.54" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M19.07 4.93C20.94 6.8 22.01 9.35 22.01 12C22.01 14.65 20.94 17.2 19.07 19.07" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span class="a11y-label">Screen<br>Reader</span>
        </div>

        <div class="a11y-item" :class="{ active: smartContrastOn }" @click="toggleSmartContrast()">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5" />
            <path d="M12 3V21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3Z" fill="currentColor" />
          </svg>
          <span class="a11y-label">Smart<br>Contrast</span>
        </div>

        <div class="a11y-item" :class="{ active: fontSize !== 100 }">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <text x="1" y="14" font-size="14" font-weight="bold" font-family="Arial" fill="currentColor">T</text>
            <text x="12" y="17" font-size="18" font-weight="bold" font-family="Arial" fill="currentColor">T</text>
          </svg>
          <div class="a11y-label d-flex align-center justify-center" style="gap: 6px;">
            <span class="a11y-font-btn" @click="changeFontSize(-3)">A-</span>
            <span class="a11y-font-btn a11y-font-btn-plus" @click="changeFontSize(3)">A+</span>
          </div>
        </div>

        <div class="a11y-item" :class="{ active: themeTone !== '' }">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="10" r="8" stroke="currentColor" stroke-width="1.5" />
            <line x1="8" y1="6" x2="8" y2="14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            <line x1="12" y1="4" x2="12" y2="16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            <line x1="16" y1="6" x2="16" y2="14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <span class="a11y-label">Theme Tone</span>
          <div class="a11y-dots">
            <span class="a11y-dot" :class="{ 'a11y-dot-active': themeTone === 'default' }" style="background:#fff; border-color:#cdcdcd;" @click="setThemeTone('default')"></span>
            <span class="a11y-dot" :class="{ 'a11y-dot-active': themeTone === 'warm' }" style="background:#FDD835; border-color:#F9A825;" @click="setThemeTone('warm')"></span>
            <span class="a11y-dot" :class="{ 'a11y-dot-active': themeTone === 'cool' }" style="background:#66BB6A; border-color:#43A047;" @click="setThemeTone('cool')"></span>
            <span class="a11y-dot" :class="{ 'a11y-dot-active': themeTone === 'sepia' }" style="background:#EF5350; border-color:#E53935;" @click="setThemeTone('sepia')"></span>
          </div>
        </div>

        <div class="a11y-item" :class="{ active: hideImagesOn }" @click="toggleHideImages()">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5" />
            <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" stroke-width="1.5" />
            <path d="M21 15L16 10L5 21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M2 2L22 22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <span class="a11y-label">Hide<br>Images</span>
        </div>

        <div class="a11y-item" :class="{ active: invertColorsOn }" @click="toggleInvertColors()">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5" />
            <path d="M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21V3Z" fill="currentColor" />
          </svg>
          <span class="a11y-label">Invert<br>Colours</span>
        </div>

        <div class="a11y-item" :class="{ active: grayscaleOn }" @click="toggleGrayscale()">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5" />
            <rect x="3" y="3" width="9" height="18" rx="1" fill="currentColor" opacity="0.4" />
            <path d="M3 12L21 12" stroke="currentColor" stroke-width="1" />
          </svg>
          <span class="a11y-label">Grayscale</span>
        </div>

        <div class="a11y-item" :class="{ active: blueLightFilterOn }" @click="toggleBlueLightFilter()">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span class="a11y-label">Blue Light<br>Filter</span>
        </div>

        <div class="a11y-item" :class="{ active: adhdFocusOn }" @click="toggleAdhdFocus()">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.5" />
            <circle cx="12" cy="12" r="7" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 3" />
            <path d="M12 2V5M12 19V22M2 12H5M19 12H22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <span class="a11y-label">ADHD Focus<br>Assist</span>
        </div>

        <div class="a11y-item" :class="{ active: dyslexiaOn }" @click="toggleDyslexia()">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" stroke-width="1.5" />
            <path d="M8 7H16M8 11H16M8 15H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <span class="a11y-label">Dyslexia<br>Support</span>
        </div>

        <div class="a11y-item" :class="{ active: highlightLinksOn }" @click="toggleHighlightLinks()">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span class="a11y-label">Highlight<br>Links</span>
        </div>

        <div class="a11y-item" :class="{ active: focusIndicatorOn }" @click="toggleFocusIndicator()">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.5" />
            <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.5" />
            <circle cx="12" cy="12" r="1" fill="currentColor" />
          </svg>
          <span class="a11y-label">Focus<br>Indicator</span>
        </div>

        <div class="a11y-item" :class="{ active: reduceMotionOn }" @click="toggleReduceMotion()">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 12H19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            <path d="M9 8L5 12L9 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M2 2L22 22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <span class="a11y-label">Reduce<br>Motion</span>
        </div>
      </div>

      <!-- Reset All -->
      <div class="px-3 pb-3 pt-1">
        <v-btn block outlined height="42" color="#999" class="text-capitalize rounded-pill reset-btn" @click="resetAll()">
          Reset All
        </v-btn>
      </div>
    </v-card>
  </v-menu>

  </div>
</template>

<script>
export default {
  computed: {
    hasActiveSettings() {
      return (
        this.screenReaderOn ||
        this.hideImagesOn ||
        this.invertColorsOn ||
        this.grayscaleOn ||
        this.blueLightFilterOn ||
        this.smartContrastOn ||
        this.fontSize !== 100 ||
        this.themeTone !== '' ||
        this.adhdFocusOn ||
        this.focusIndicatorOn ||
        this.reduceMotionOn ||
        this.dyslexiaOn ||
        this.highlightLinksOn
      )
    },
  },
  data() {
    return {
      active: '',
      menuOpen: false,
      screenReaderOn: false,
      hideImagesOn: false,
      invertColorsOn: false,
      grayscaleOn: false,
      blueLightFilterOn: false,
      smartContrastOn: false,
      fontSize: 100,
      fontBump: 0,
      themeTone: '',
      adhdFocusOn: false,
      focusIndicatorOn: false,
      reduceMotionOn: false,
      dyslexiaOn: false,
      highlightLinksOn: false,
    }
  },
  beforeDestroy() {
    this.disableScreenReader()
    this.disableHideImages()
    document.documentElement.classList.remove('a11y-invert-colors')
    document.documentElement.classList.remove('a11y-grayscale')
    document.documentElement.classList.remove('a11y-blue-light-filter')
    document.documentElement.classList.remove('a11y-focus-indicator')
    document.documentElement.classList.remove('a11y-reduce-motion')
    this.removeThemeOverlay()
    this.removeAdhdOverlay()
    document.removeEventListener('mousemove', this.trackAdhdMouse)
    document.documentElement.classList.remove('a11y-smart-contrast')
    document.documentElement.classList.remove('a11y-dyslexia')
    document.documentElement.classList.remove('a11y-highlight-links')
    this.removeFontScale()
  },
  methods: {
    toggle(name) {
      this.active = this.active === name ? '' : name
    },

    toggleAdhdFocus() {
      this.adhdFocusOn = !this.adhdFocusOn
      this.menuOpen = false
      if (this.adhdFocusOn) {
        this.createAdhdOverlay()
        document.addEventListener('mousemove', this.trackAdhdMouse)
      } else {
        this.removeAdhdOverlay()
        document.removeEventListener('mousemove', this.trackAdhdMouse)
      }
    },

    createAdhdOverlay() {
      this.removeAdhdOverlay()
      const overlay = document.createElement('div')
      overlay.id = 'adhd-focus-top'
      overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:0px;background:rgba(0,0,0,0.45);pointer-events:none;z-index:99998;'
      document.body.appendChild(overlay)
    },

    removeAdhdOverlay() {
      const el = document.getElementById('adhd-focus-top')
      if (el) el.remove()
    },

    trackAdhdMouse(e) {
      const el = document.getElementById('adhd-focus-top')
      if (el) el.style.height = e.clientY + 'px'
    },

    toggleHighlightLinks() {
      this.highlightLinksOn = !this.highlightLinksOn
      document.documentElement.classList.toggle('a11y-highlight-links', this.highlightLinksOn)
    },

    toggleDyslexia() {
      this.dyslexiaOn = !this.dyslexiaOn
      document.documentElement.classList.toggle('a11y-dyslexia', this.dyslexiaOn)
    },

    toggleSmartContrast() {
      this.smartContrastOn = !this.smartContrastOn
      document.documentElement.classList.toggle('a11y-smart-contrast', this.smartContrastOn)
    },

    setThemeTone(tone) {
      if (this.themeTone === tone) {
        this.themeTone = ''
        this.removeThemeOverlay()
      } else {
        this.themeTone = tone
        this.applyThemeOverlay(tone)
      }
    },

    applyThemeOverlay(tone) {
      this.removeThemeOverlay()
      document.documentElement.classList.add('a11y-tone-' + tone)
    },

    removeThemeOverlay() {
      document.documentElement.classList.remove('a11y-tone-default', 'a11y-tone-warm', 'a11y-tone-cool', 'a11y-tone-sepia')
    },

    changeFontSize(step) {
      const next = this.fontBump + step
      if (next < -20 || next > 50) return
      this.fontBump = next
      this.fontSize = 100 + this.fontBump
      this.applyFontScale()
    },

    applyFontScale() {
      const bump = this.fontBump
      if (bump === 0) {
        this.removeFontScale()
        return
      }
      const skip = (el) => el.closest('.a11y-card') || el.closest('.a11y-keep') || el.closest('.v-menu__content')
      const els = document.body.querySelectorAll('h1,h2,h3,h4,h5,h6,p,a,span,li,td,th,label,button,input,textarea,select,small,strong,em,b,i,div,.v-btn__content,.v-tab,.v-chip,.v-badge__badge,.v-card__title,.v-card__text,.v-list-item__title,.v-list-item__subtitle')
      els.forEach(el => {
        if (skip(el)) return
        if (el.tagName === 'DIV' && !this.hasDirectText(el)) return
        let orig = el.getAttribute('data-a11y-orig-fs')
        if (!orig) {
          orig = parseFloat(window.getComputedStyle(el).fontSize)
          el.setAttribute('data-a11y-orig-fs', orig)
        }
        el.style.setProperty('font-size', (parseFloat(orig) + bump) + 'px', 'important')
      })
    },

    hasDirectText(el) {
      for (const node of el.childNodes) {
        if (node.nodeType === 3 && node.textContent.trim()) return true
      }
      return false
    },

    removeFontScale() {
      document.querySelectorAll('[data-a11y-orig-fs]').forEach(el => {
        el.style.removeProperty('font-size')
        el.removeAttribute('data-a11y-orig-fs')
      })
    },
    closeMenu() {
      this.menuOpen = false
    },

    resetAll() {
      this.active = ''
      this.fontSize = 100
      this.fontBump = 0
      this.themeTone = ''
      this.smartContrastOn = false
      this.focusIndicatorOn = false
      this.reduceMotionOn = false
      this.dyslexiaOn = false
      this.highlightLinksOn = false
      document.documentElement.style.fontSize = '100%'
      this.removeFontScale()
      document.documentElement.classList.remove('a11y-highlight-links')
      document.documentElement.classList.remove('a11y-smart-contrast')
      document.documentElement.classList.remove('a11y-focus-indicator')
      document.documentElement.classList.remove('a11y-reduce-motion')
      document.documentElement.classList.remove('a11y-dyslexia')
      this.removeThemeOverlay()
      if (this.screenReaderOn) {
        this.screenReaderOn = false
        this.disableScreenReader()
      }
      if (this.hideImagesOn) {
        this.hideImagesOn = false
        this.disableHideImages()
      }
      if (this.adhdFocusOn) {
        this.adhdFocusOn = false
        this.removeAdhdOverlay()
        document.removeEventListener('mousemove', this.trackAdhdMouse)
      }
      this.clearVisualFilters()
    },

    clearVisualFilters() {
      this.invertColorsOn = false
      this.grayscaleOn = false
      this.blueLightFilterOn = false
      document.documentElement.classList.remove('a11y-invert-colors', 'a11y-grayscale', 'a11y-blue-light-filter')
    },

    toggleFocusIndicator() {
      this.focusIndicatorOn = !this.focusIndicatorOn
      document.documentElement.classList.toggle('a11y-focus-indicator', this.focusIndicatorOn)
    },

    toggleReduceMotion() {
      this.reduceMotionOn = !this.reduceMotionOn
      document.documentElement.classList.toggle('a11y-reduce-motion', this.reduceMotionOn)
    },

    toggleBlueLightFilter() {
      const next = !this.blueLightFilterOn
      this.clearVisualFilters()
      if (next) {
        this.blueLightFilterOn = true
        document.documentElement.classList.add('a11y-blue-light-filter')
      }
    },

    toggleGrayscale() {
      const next = !this.grayscaleOn
      this.clearVisualFilters()
      if (next) {
        this.grayscaleOn = true
        document.documentElement.classList.add('a11y-grayscale')
      }
    },

    toggleInvertColors() {
      const next = !this.invertColorsOn
      this.clearVisualFilters()
      if (next) {
        this.invertColorsOn = true
        document.documentElement.classList.add('a11y-invert-colors')
      }
    },

    toggleHideImages() {
      this.hideImagesOn = !this.hideImagesOn
      if (this.hideImagesOn) {
        this.enableHideImages()
      } else {
        this.disableHideImages()
      }
    },

    enableHideImages() {
      document.body.classList.add('a11y-hide-images')
    },

    disableHideImages() {
      document.body.classList.remove('a11y-hide-images')
      if (this.adhdFocusOn) {
        this.adhdFocusOn = false
        this.removeAdhdOverlay()
        document.removeEventListener('mousemove', this.trackAdhdMouse)
      }
    },

    toggleScreenReader() {
      this.screenReaderOn = !this.screenReaderOn
      this.menuOpen = false

      if (this.screenReaderOn) {
        this.enableScreenReader()
      } else {
        this.disableScreenReader()
      }
    },

    enableScreenReader() {
      document.addEventListener('mouseover', this.handleHover)
    },

    disableScreenReader() {
      window.speechSynthesis.cancel()
      document.removeEventListener('mouseover', this.handleHover)
    },

    handleHover(e) {
      const el = e.target
      if (!el || el.closest('.a11y-card') || el.closest('.v-menu__content')) return

      const text = this.getReadableText(el)
      if (text) {
        window.speechSynthesis.cancel()
        this.speak(text)
      }
    },

    getReadableText(el) {
      const alt = el.getAttribute('alt')
      const ariaLabel = el.getAttribute('aria-label')
      const innerText = el.innerText || el.textContent || ''
      return ariaLabel || alt || innerText.trim()
    },

    speak(text) {
      window.speechSynthesis.cancel()
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = 'en-IN'
      utterance.rate = 0.9
      window.speechSynthesis.speak(utterance)
    },
  },
}
</script>

<style>
/* Smart Contrast - Universal dark theme for all pages */

/* ── 1. All text → pure white ─────────────────────────────────────────────── */
.a11y-smart-contrast * {
  color: #ffffff !important;
  border-color: #30363d !important;
}
/* Vuetify dark-text helper overrides */
.a11y-smart-contrast .black--text,
.a11y-smart-contrast .black--text *,
.a11y-smart-contrast .grey--text,
.a11y-smart-contrast .grey--text *,
.a11y-smart-contrast .v-label,
.a11y-smart-contrast .v-list-item__title,
.a11y-smart-contrast .v-list-item__subtitle,
.a11y-smart-contrast .theme--light.v-label,
.a11y-smart-contrast .theme--light .v-label,
.a11y-smart-contrast .v-input input,
.a11y-smart-contrast .v-input textarea,
.a11y-smart-contrast .v-select__selection,
.a11y-smart-contrast .v-text-field input {
  color: #ffffff !important;
}
/* Grey text utility classes → white in smart contrast */
.a11y-smart-contrast .txt-444,
.a11y-smart-contrast .txt-666,
.a11y-smart-contrast .txt-666666,
.a11y-smart-contrast .txt-999 {
  color: #ffffff !important;
}
.a11y-smart-contrast body,
.a11y-smart-contrast .v-application,
.a11y-smart-contrast .v-application--wrap,
.a11y-smart-contrast .v-main,
.a11y-smart-contrast .v-main__wrap {
  background-color: #0d1117 !important;
}
.a11y-smart-contrast .v-app-bar,
.a11y-smart-contrast .cust-appbar {
  background-color: #161b22 !important;
}
.a11y-smart-contrast .v-card,
.a11y-smart-contrast .v-sheet,
.a11y-smart-contrast .v-list,
.a11y-smart-contrast .v-expansion-panel,
.a11y-smart-contrast .v-dialog,
.a11y-smart-contrast .v-data-table,
.a11y-smart-contrast .v-chip {
  background-color: #161b22 !important;
}
/* Elements inside gradient/colored sections keep original look */
.a11y-smart-contrast .bgg-lin .v-card,
.a11y-smart-contrast .bgg-linnew .v-card,
.a11y-smart-contrast .support-main-bg .v-card,
.a11y-smart-contrast .bgg-lin .v-sheet,
.a11y-smart-contrast .bgg-linnew .v-sheet,
.a11y-smart-contrast .support-main-bg .v-sheet {
  background-color: transparent !important;
}
.a11y-smart-contrast .bgg-lin .v-input,
.a11y-smart-contrast .bgg-linnew .v-input,
.a11y-smart-contrast .support-main-bg .v-input,
.a11y-smart-contrast .bgg-lin .v-text-field,
.a11y-smart-contrast .bgg-linnew .v-text-field,
.a11y-smart-contrast .support-main-bg .v-text-field,
.a11y-smart-contrast .bgg-lin .v-input__control,
.a11y-smart-contrast .bgg-linnew .v-input__control,
.a11y-smart-contrast .support-main-bg .v-input__control,
.a11y-smart-contrast .bgg-lin .v-input__slot,
.a11y-smart-contrast .bgg-linnew .v-input__slot,
.a11y-smart-contrast .support-main-bg .v-input__slot,
.a11y-smart-contrast .bgg-lin .v-text-field__details,
.a11y-smart-contrast .bgg-linnew .v-text-field__details,
.a11y-smart-contrast .support-main-bg .v-text-field__details {
  background-color: transparent !important;
}
.a11y-smart-contrast .bgg-lin .v-input__slot,
.a11y-smart-contrast .bgg-linnew .v-input__slot,
.a11y-smart-contrast .support-main-bg .v-input__slot {
  background-color: #ffffff !important;
}
.a11y-smart-contrast .bgg-lin .v-text-field input,
.a11y-smart-contrast .bgg-linnew .v-text-field input,
.a11y-smart-contrast .support-main-bg .v-text-field input {
  color: #333 !important;
}
.a11y-smart-contrast .bgg-lin .v-messages,
.a11y-smart-contrast .bgg-linnew .v-messages,
.a11y-smart-contrast .support-main-bg .v-messages {
  color: #ff6b6b !important;
}
.a11y-smart-contrast .v-footer,
.a11y-smart-contrast .v-footer.white,
.a11y-smart-contrast .v-footer.theme--light,
.a11y-smart-contrast footer,
.a11y-smart-contrast .sec-footer,
.a11y-smart-contrast .v-footer.v-sheet {
  background-color: #0d1117 !important;
}
.a11y-smart-contrast .v-footer .v-card,
.a11y-smart-contrast .sec-footer .v-card {
  background-color: #0d1117 !important;
}
.a11y-smart-contrast .v-input,
.a11y-smart-contrast .v-text-field,
.a11y-smart-contrast .v-autocomplete {
  background-color: #21262d !important;
}
/* Hero and gradient sections keep color but darker */
.a11y-smart-contrast .bgg-lin,
.a11y-smart-contrast .bgg-linnew {
  background: linear-gradient(246deg, #001d4d 13%, #002a7a 57%, #003399 89%) !important;
}
.a11y-smart-contrast .support-main-bg {
  background: linear-gradient(400deg, #001d4d 0, #002a7a 68%, #003399 97%) !important;
}
.a11y-smart-contrast a {
  color: #58a6ff !important;
}
.a11y-smart-contrast .white--text,
.a11y-smart-contrast .white--text * {
  color: #ffffff !important;
}
/* ── 2. All inline SVGs → white via filter ────────────────────────────────── */
.a11y-smart-contrast svg {
  filter: brightness(0) invert(1) !important;
}

/* ── 3. Nav/menu icon images only → white (NOT regular photos/banners) ───────── */
.a11y-smart-contrast .app-menu-items img {
  filter: brightness(0) invert(1) !important;
}

/* ── 4. EXCEPTION: Zebu brand logo — keep original blue ─────────────────────── */
.a11y-smart-contrast .zebu-logo svg {
  filter: none !important;
}

/* ── 5. EXCEPTION: Hero/gradient sections — already visible, skip filter ──────── */
.a11y-smart-contrast .bgg-lin svg,
.a11y-smart-contrast .bgg-linnew svg,
.a11y-smart-contrast .support-main-bg svg {
  filter: none !important;
}

/* ── 6. EXCEPTION: Accessibility widget — SVG icons use currentColor (white) ─── */
.a11y-smart-contrast .a11y-card svg,
.a11y-smart-contrast .a11y-card img {
  filter: none !important;
}

/* ── Accessibility widget dark styling ────────────────────────────────────── */
.a11y-smart-contrast .a11y-card {
  background-color: #1c2128 !important;
}
.a11y-smart-contrast .a11y-card * {
  background-color: transparent !important;
}
.a11y-smart-contrast .a11y-header {
  background-color: #161b22 !important;
}
.a11y-smart-contrast .a11y-item {
  background-color: #21262d !important;
  border-color: #444c56 !important;
  color: #666666 !important;
}
.a11y-smart-contrast .a11y-item:hover {
  background-color: #2d333b !important;
  color: #0037B7 !important;
}
.a11y-smart-contrast .a11y-item.active {
  background-color: #1f3a6e !important;
  border-color: #58a6ff !important;
  color: #0037B7 !important;
}
/* SVG icons: no filter, use original currentColor from parent */
.a11y-smart-contrast .a11y-item svg {
  filter: none !important;
}
.a11y-smart-contrast .a11y-item .a11y-label,
.a11y-smart-contrast .a11y-item .a11y-font-btn {
  color: #cccccc !important;
}
.a11y-smart-contrast .a11y-dot {
  border-color: transparent !important;
}
.a11y-smart-contrast .a11y-dot-active {
  border-color: #58a6ff !important;
}

/* ── EXCEPTION: Support page accordion — bypass smart contrast fully ─────── */
.a11y-smart-contrast .accordion-container,
.a11y-smart-contrast .accordion-section {
  background-color: transparent !important;
}
.a11y-smart-contrast .accordion-header {
  background-color: #f5f5f5 !important;
  border-color: #e0e0e0 !important;
}
.a11y-smart-contrast .accordion-header.expanded {
  background-color: #e3f2fd !important;
}
.a11y-smart-contrast .accordion-title,
.a11y-smart-contrast .accordion-title * {
  color: #333333 !important;
}
.a11y-smart-contrast .accordion-content {
  background-color: #ffffff !important;
}
.a11y-smart-contrast .accordion-content-inner {
  background-color: #ffffff !important;
}
.a11y-smart-contrast .faq-list {
  background-color: #ffffff !important;
}
.a11y-smart-contrast .faq-item {
  color: #000000 !important;
  background-color: #ffffff !important;
  border-color: #f0f0f0 !important;
}
.a11y-smart-contrast .faq-item:hover {
  color: #0d47a1 !important;
}
/* Keep accordion chevron SVGs their original blue — no filter */
.a11y-smart-contrast .accordion-chevron svg {
  filter: none !important;
}

/* ── EXCEPTION: Support page Quick Links card — bypass smart contrast ─────── */
.a11y-smart-contrast .support-quick-links,
.a11y-smart-contrast .support-quick-links .v-card {
  background-color: #ffffff !important;
  border-color: #ebeef0 !important;
}
.a11y-smart-contrast .support-quick-links *,
.a11y-smart-contrast .support-quick-links .v-card * {
  color: #111111 !important;
  background-color: transparent !important;
}
.a11y-smart-contrast .support-quick-links .v-card__title,
.a11y-smart-contrast .support-quick-links .v-card-title {
  color: #000000 !important;
}

/* Theme Tone - tint page + surfaces so the selected color is visible */
html.a11y-tone-default,
html.a11y-tone-default body,
html.a11y-tone-default .v-application,
html.a11y-tone-default .v-application--wrap,
html.a11y-tone-default .v-main,
html.a11y-tone-default .v-main__wrap,
html.a11y-tone-default .v-card,
html.a11y-tone-default .v-sheet {
  background-color: #ffffff !important;
}

html.a11y-tone-warm,
html.a11y-tone-warm body,
html.a11y-tone-warm .v-application,
html.a11y-tone-warm .v-application--wrap,
html.a11y-tone-warm .v-main,
html.a11y-tone-warm .v-main__wrap,
html.a11y-tone-warm .v-card,
html.a11y-tone-warm .v-sheet {
  background-color: #FFF8E1 !important;
}

html.a11y-tone-cool,
html.a11y-tone-cool body,
html.a11y-tone-cool .v-application,
html.a11y-tone-cool .v-application--wrap,
html.a11y-tone-cool .v-main,
html.a11y-tone-cool .v-main__wrap,
html.a11y-tone-cool .v-card,
html.a11y-tone-cool .v-sheet {
  background-color: #E8F5E9 !important;
}

html.a11y-tone-sepia,
html.a11y-tone-sepia body,
html.a11y-tone-sepia .v-application,
html.a11y-tone-sepia .v-application--wrap,
html.a11y-tone-sepia .v-main,
html.a11y-tone-sepia .v-main__wrap,
html.a11y-tone-sepia .v-card,
html.a11y-tone-sepia .v-sheet {
  background-color: #FBE9E7 !important;
}

/* Keep the accessibility widget itself on white so the tone dots stay readable */
html[class*="a11y-tone-"] .a11y-card,
html[class*="a11y-tone-"] .a11y-item {
  background-color: #ffffff !important;
}
html[class*="a11y-tone-"] .a11y-header {
  background-color: #F1F3F8 !important;
}
html[class*="a11y-tone-"] .a11y-item.active {
  background-color: #EEF2FF !important;
}


/* Highlight Links */
.a11y-highlight-links a,
.a11y-highlight-links .v-card[href],
.a11y-highlight-links .v-card[to],
.a11y-highlight-links .v-list-item[href],
.a11y-highlight-links .v-list-item[to] {
  background-color: #FFD600 !important;
  text-decoration: underline !important;
  color: #0037B7 !important;
}

/* Dyslexia Support */
.a11y-dyslexia,
.a11y-dyslexia body,
.a11y-dyslexia .v-application,
.a11y-dyslexia .v-application * {
  font-family: 'OpenDyslexic', 'Comic Sans MS', 'Arial', sans-serif !important;
  letter-spacing: 0.02em !important;
  word-spacing: 0em !important;
}
html.a11y-dyslexia .v-application input,
html.a11y-dyslexia .v-application textarea,
html.a11y-dyslexia .v-application select,
html.a11y-dyslexia .v-application button,
html.a11y-dyslexia .v-application .v-btn,
html.a11y-dyslexia .v-application .v-label,
html.a11y-dyslexia .v-application .v-input,
html.a11y-dyslexia .v-application .v-input input,
html.a11y-dyslexia .v-application .v-input textarea,
html.a11y-dyslexia .v-application .v-select__selection,
html.a11y-dyslexia .v-application .v-messages,
html.a11y-dyslexia .v-application .v-messages__message,
html.a11y-dyslexia .v-application .v-list-item__title,
html.a11y-dyslexia .v-application .v-list-item__subtitle,
html.a11y-dyslexia .v-application .title,
html.a11y-dyslexia .v-application .subtitle-1,
html.a11y-dyslexia .v-application .subtitle-2,
html.a11y-dyslexia .v-application .body-1,
html.a11y-dyslexia .v-application .body-2,
html.a11y-dyslexia .v-application .caption,
html.a11y-dyslexia .v-application .overline,
html.a11y-dyslexia .v-application .display-1,
html.a11y-dyslexia .v-application .display-2,
html.a11y-dyslexia .v-application .headline,
html.a11y-dyslexia .v-application .font-weight-black,
html.a11y-dyslexia .v-application .font-weight-bold,
html.a11y-dyslexia .v-application .font-weight-medium,
html.a11y-dyslexia .v-application [class*='text'],
html.a11y-dyslexia .v-application [class*='font-weight'],
html.a11y-dyslexia .v-application .sec-fn {
  font-family: 'OpenDyslexic', 'Comic Sans MS', 'Arial', sans-serif !important;
}
.a11y-dyslexia input::placeholder,
.a11y-dyslexia textarea::placeholder {
  font-family: 'OpenDyslexic', 'Comic Sans MS', 'Arial', sans-serif !important;
}
.a11y-dyslexia p,
.a11y-dyslexia span,
.a11y-dyslexia li,
.a11y-dyslexia label,
.a11y-dyslexia td,
.a11y-dyslexia a,
.a11y-dyslexia div,
.a11y-dyslexia small {
  line-height: 1.6 !important;
}
.a11y-dyslexia h1, .a11y-dyslexia h2,
.a11y-dyslexia h3, .a11y-dyslexia h4,
.a11y-dyslexia h5, .a11y-dyslexia h6 {
  line-height: 1.3 !important;
}

/* Theme Tones */




/* Hide Images Accessibility */
.a11y-hide-images img {
  visibility: hidden !important;
}
.a11y-hide-images svg {
  visibility: hidden !important;
}
.a11y-hide-images picture {
  visibility: hidden !important;
}
.a11y-hide-images video {
  visibility: hidden !important;
}
.a11y-hide-images [style*="background-image"] {
  background-image: none !important;
}
/* Keep accessibility widget & menu bar icons visible */
.a11y-hide-images .a11y-keep img,
.a11y-hide-images .a11y-keep svg,
.a11y-hide-images .a11y-card img,
.a11y-hide-images .a11y-card svg,
.a11y-hide-images .v-menu__content img,
.a11y-hide-images .v-menu__content svg {
  visibility: visible !important;
}
.a11y-hide-images [aria-label="Accessibility Menu"] svg,
.a11y-hide-images [alt="apps"] {
  visibility: visible !important;
}

/* Invert Colors Accessibility — full invert, no image exceptions */
html.a11y-invert-colors {
  filter: invert(1) hue-rotate(180deg);
  background-color: #000;
}

/* Grayscale Accessibility */
html.a11y-grayscale {
  filter: grayscale(1);
}

/* Blue Light Filter Accessibility */
html.a11y-blue-light-filter {
  filter: sepia(0.4) saturate(0.8) brightness(0.95);
}

/* Hide Images Accessibility */
.a11y-hide-images img {
  visibility: hidden !important;
}
.a11y-hide-images svg {
  visibility: hidden !important;
}
.a11y-hide-images picture {
  visibility: hidden !important;
}
.a11y-hide-images video {
  visibility: hidden !important;
}
.a11y-hide-images [style*="background-image"] {
  background-image: none !important;
}
/* Keep accessibility widget & menu bar icons visible */
.a11y-hide-images .a11y-keep img,
.a11y-hide-images .a11y-keep svg,
.a11y-hide-images .a11y-card img,
.a11y-hide-images .a11y-card svg,
.a11y-hide-images .v-menu__content img,
.a11y-hide-images .v-menu__content svg {
  visibility: visible !important;
}
.a11y-hide-images [aria-label="Accessibility Menu"] svg,
.a11y-hide-images [alt="apps"] {
  visibility: visible !important;
}

/* Invert Colors Accessibility — full invert, no image exceptions */
html.a11y-invert-colors {
  filter: invert(1) hue-rotate(180deg);
  background-color: #000;
}

/* Grayscale Accessibility */
html.a11y-grayscale {
  filter: grayscale(1);
}

/* Blue Light Filter Accessibility */
html.a11y-blue-light-filter {
  filter: sepia(0.4) saturate(0.8) brightness(0.95);
}

/* ─── Focus Indicator ─────────────────────────────────────────────────────── */
/* Show a strong, clearly visible focus ring on every interactive element      */
html.a11y-focus-indicator *:focus,
html.a11y-focus-indicator *:focus-visible {
  outline: 3px solid #0037B7 !important;
  outline-offset: 3px !important;
  box-shadow: 0 0 0 5px rgba(0, 55, 183, 0.2) !important;
  border-radius: 3px;
}
/* Ensure Vuetify components that suppress outline also get the ring */
html.a11y-focus-indicator .v-btn:focus,
html.a11y-focus-indicator .v-btn:focus-visible,
html.a11y-focus-indicator .v-tab:focus,
html.a11y-focus-indicator .v-tab:focus-visible,
html.a11y-focus-indicator .v-list-item:focus,
html.a11y-focus-indicator .v-list-item:focus-visible,
html.a11y-focus-indicator a:focus,
html.a11y-focus-indicator a:focus-visible,
html.a11y-focus-indicator input:focus,
html.a11y-focus-indicator textarea:focus,
html.a11y-focus-indicator select:focus,
html.a11y-focus-indicator [tabindex]:focus,
html.a11y-focus-indicator [role="button"]:focus,
html.a11y-focus-indicator [role="link"]:focus,
html.a11y-focus-indicator [role="menuitem"]:focus,
html.a11y-focus-indicator [role="option"]:focus,
html.a11y-focus-indicator [role="tab"]:focus,
html.a11y-focus-indicator [role="checkbox"]:focus,
html.a11y-focus-indicator [role="radio"]:focus {
  outline: 3px solid #0037B7 !important;
  outline-offset: 3px !important;
  box-shadow: 0 0 0 5px rgba(0, 55, 183, 0.2) !important;
}

/* ─── Reduce Motion ───────────────────────────────────────────────────────── */
/* Suppress all CSS animations, transitions, and scroll effects site-wide     */
html.a11y-reduce-motion,
html.a11y-reduce-motion * {
  scroll-behavior: auto !important;
}
html.a11y-reduce-motion *,
html.a11y-reduce-motion *::before,
html.a11y-reduce-motion *::after {
  animation-duration: 0.01ms !important;
  animation-delay: 0.01ms !important;
  animation-iteration-count: 1 !important;
  transition-duration: 0.01ms !important;
  transition-delay: 0.01ms !important;
}
/* Vuetify-specific transition classes */
html.a11y-reduce-motion .v-enter-active,
html.a11y-reduce-motion .v-leave-active,
html.a11y-reduce-motion .v-move,
html.a11y-reduce-motion [class*="-enter-active"],
html.a11y-reduce-motion [class*="-leave-active"],
html.a11y-reduce-motion [class*="-move"] {
  transition-duration: 0.01ms !important;
  animation-duration: 0.01ms !important;
}
</style>

<style scoped>
/* ── Card ─────────────────────────────────────────────────────────────────── */
.a11y-card {
  border-radius: 16px !important;
  border: 1.5px solid rgba(0, 55, 183, 0.151) !important;
  box-shadow: 0 8px 32px rgba(0, 55, 183, 0.15) !important;
  overflow: hidden;
  font-family: 'tenon', sans-serif;
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.a11y-header {
  background-color: #F1F3F8;
  border-bottom: 1.5px solid #EBEEF0;
}

.a11y-header-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #0037B7;
  display: inline-block;
  flex-shrink: 0;
}

.a11y-title {
  font-size: 15px;
  font-weight: 700;
  color: #0037B7;
  letter-spacing: -0.2px;
}

.a11y-close-btn {
  color: #0037B7 !important;
}

/* ── Grid (scrollable, 2 rows visible) ────────────────────────────────────── */
.a11y-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  max-height: 186px;
  overflow-y: auto;
  padding-right: 2px;
  scrollbar-width: thin;
  scrollbar-color: #CFD9F2 transparent;
}
.a11y-grid::-webkit-scrollbar {
  width: 4px;
}
.a11y-grid::-webkit-scrollbar-track {
  background: transparent;
}
.a11y-grid::-webkit-scrollbar-thumb {
  background-color: #CFD9F2;
  border-radius: 4px;
}

/* ── Item ─────────────────────────────────────────────────────────────────── */
.a11y-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 4px 10px;
  border-radius: 10px;
  border: 1.5px solid #EBEEF0;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  min-height: 85px;
  background: #fff;
  color: #666666;
}

.a11y-item:hover {
  background-color: #F1F3F8;
  border-color: #CFD9F2;
  color: #0037B7;
}

.a11y-item.active {
  border-color: #0037B7;
  background-color: #EEF2FF;
  color: #0037B7;
}

/* ── Label — inherits color from parent item ──────────────────────────────── */
.a11y-label {
  font-size: 11px;
  text-align: center;
  line-height: 1.25;
  font-weight: 500;
  margin-top: 6px;
  color: inherit;
}

/* ── Theme Tone dots ──────────────────────────────────────────────────────── */
.a11y-dots {
  display: flex;
  gap: 4px;
  margin-top: 4px;
}

.a11y-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  cursor: pointer;
  border: 2px solid;
  transition: all 0.15s ease;
}

.a11y-dot:hover {
  transform: scale(1.2);
}

.a11y-dot-active {
  border: 2px solid #0037B7 !important;
  transform: scale(1.2);
}

/* ── Font size buttons ────────────────────────────────────────────────────── */
.a11y-font-btn {
  font-size: 12px;
  font-weight: 600;
  color: inherit;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.a11y-font-btn:hover {
  background-color: #CFD9F2;
  color: #0037B7;
}

.a11y-font-btn-plus {
  font-size: 14px;
}

/* ── Reset button ─────────────────────────────────────────────────────────── */
.reset-btn {
  font-size: 14px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  border-color: #dce3f3 !important;
  color: #0037B7 !important;
}

/* ── Pentagon background for accessibility icon ───────────────────────────── */
.a11y-pentagon-bg {
  position: relative;
  width: 36px;
  height: 36px;
  background-color: #0037B7;
  clip-path: polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}
.menu-app-btn:hover .a11y-pentagon-bg {
  background-color: #0030a0;
}

.a11y-trigger-dot {
  position: absolute;
  right: 1px;
  bottom: 1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #ffb200;
  border: 2px solid #ffffff;
}

/* ── Invisible spacers (Reduce Motion row) ────────────────────────────────── */
.a11y-spacer {
  flex: 1;
  min-height: 85px;
  visibility: hidden;
  pointer-events: none;
}
</style>
